import { FC, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Grid,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
  Paper,
} from '@mui/material';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledDateField from '../../../../components/controlledComponents/ControlledDateField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { useAcademicsService } from '../../../../../apis/academics/useAcademicsService';
import { IAssessmentRequest } from '../../../../../apis/academics/academicsAssessments.interfaces';
import dayjs, { Dayjs } from 'dayjs';
import { useClassGradeSubjectStructureContext } from '../../../institute/contexts/classGradeSubjectStructure.context';
import { useStudentListHook } from '../../../students/hooks/useStudentListHook';
import { useAcademicsAssessmentsContext } from '../../contexts/academicsAssessments.context';
import { useAcademicsAssessmentDetailsHook } from '../../hooks/useAcademicsAssessmentsDetailsHook';
import {
  zNumberFromInput,
  zOptionalNullablePickerDate,
  zOptionalNumberFromInput,
} from 'src/utils/zodFormHelpers';

type SubjectScoreConfig = {
  courseId: number;
  courseName: string;
  subjectCode?: string;
  maxScore: number; // Subject-specific max score (defaults to global maxScore)
  passingScore: number; // Subject-specific passing score (defaults to global passingScore)
  scheduledDuration?: number; // Duration in minutes (for IAssessmentSubject)
  scheduledDate?: Dayjs | null; // Scheduled date for this subject
};

type AssessmentFormData = {
  instituteId: string;
  name: string;
  description?: string;
  assessmentType:
  | 'quiz'
  | 'assignment'
  | 'exam'
  | 'project'
  | 'midterm'
  | 'final'
  | 'class_test'
  | 'homework';
  status?: 'draft' | 'scheduled' | 'active' | 'completed' | 'graded';
  classId?: number;
  courseId?: number;
  maxScore: number; // Global max score (default for all subjects)
  passingScore: number; // Global passing score (default for all subjects)
  dueDate?: Dayjs | null;
  weightage?: number;
  instructions?: string;
  remarks?: string; // Assessment-level remarks
  academicYear: string;
  subjectIds: number[]; // Selected subject IDs
  studentIds: string[]; // Selected student IDs (empty array means all students)
  subjectScoreConfigs?: SubjectScoreConfig[]; // Per-subject score configuration
};

interface AddEditAssessmentViewProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  assessmentId?: string | null;
  preSelectedClassId?: number;
  classOptions: TKeyValue<string | number>[];
}

const getAssessmentFormSchema = (translateText: (key: string) => string) =>
  z
    .object({
      instituteId: z.string().min(1, translateText('common.fieldRequired')),
      name: z.string().min(1, translateText('common.fieldRequired')),
      description: z.string().optional(),
      assessmentType: z.enum([
        'quiz',
        'assignment',
        'exam',
        'project',
        'midterm',
        'final',
        'class_test',
        'homework',
      ]),
      status: z
        .enum(['draft', 'scheduled', 'active', 'completed', 'graded'])
        .optional(),
      classId: zOptionalNumberFromInput(),
      courseId: zOptionalNumberFromInput(),
      maxScore: zNumberFromInput({
        min: 0,
        message: translateText('common.fieldRequired'),
      }),
      passingScore: zNumberFromInput({
        min: 0,
        message: translateText('common.fieldRequired'),
      }),
      dueDate: zOptionalNullablePickerDate({
        message: translateText('common.dateInvalid') || undefined,
      }),
      weightage: zOptionalNumberFromInput({
        min: 0,
        max: 100,
        message: translateText('common.fieldRequired'),
      }),
      instructions: z.string().optional(),
      remarks: z.string().optional(),
      academicYear: z.string().min(1, translateText('common.fieldRequired')),
      subjectIds: z
        .array(z.coerce.number())
        .min(
          1,
          translateText('academics.assessments.atLeastOneSubjectRequired') ||
            'At least one subject must be selected'
        ),
      studentIds: z.array(z.string()),
      subjectScoreConfigs: z
        .array(
          z.object({
            courseId: zNumberFromInput(),
            courseName: z.string(),
            subjectCode: z.string().optional(),
            maxScore: zNumberFromInput({ min: 0 }),
            passingScore: zNumberFromInput({ min: 0 }),
            scheduledDuration: zOptionalNumberFromInput({ min: 0 }),
            scheduledDate: zOptionalNullablePickerDate({
              message: translateText('common.dateInvalid') || undefined,
            }),
          })
        )
        .optional(),
    })
    .refine(
      (data) => data.classId,
      {
        message: translateText('academics.assessments.classRequired'),
        path: ['classId'],
      }
    );

const getAssessmentTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    { key: 'quiz', value: translateText('academics.assessments.types.quiz') },
    {
      key: 'assignment',
      value: translateText('academics.assessments.types.assignment'),
    },
    { key: 'exam', value: translateText('academics.assessments.types.exam') },
    {
      key: 'project',
      value: translateText('academics.assessments.types.project'),
    },
    {
      key: 'midterm',
      value: translateText('academics.assessments.types.midterm'),
    },
    {
      key: 'final',
      value: translateText('academics.assessments.types.final'),
    },
    {
      key: 'class_test',
      value: translateText('academics.assessments.types.classTest'),
    },
    {
      key: 'homework',
      value: translateText('academics.assessments.types.homework'),
    },
  ];

const getStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    { key: 'draft', value: translateText('academics.assessments.status.draft') },
    {
      key: 'scheduled',
      value: translateText('academics.assessments.status.scheduled'),
    },
    {
      key: 'active',
      value: translateText('academics.assessments.status.active'),
    },
    {
      key: 'completed',
      value: translateText('academics.assessments.status.completed'),
    },
    {
      key: 'graded',
      value: translateText('academics.assessments.status.graded'),
    },
  ];

const AddEditAssessmentView: FC<AddEditAssessmentViewProps> = ({
  open,
  onClose,
  onSuccess,
  assessmentId,
  preSelectedClassId,
  classOptions,
}) => {
  const { translateText } = useTranslation();
  const { createAssessment, updateAssessment } = useAcademicsService();
  const { getSubjectsByClassId } = useClassGradeSubjectStructureContext();
  const { getClassAssessmentById } = useAcademicsAssessmentsContext();

  // Load assessment details when editing
  const { data: assessmentDetails } = useAcademicsAssessmentDetailsHook(
    assessmentId || '',
    {
      enabled: !!assessmentId && open,
    } as Parameters<typeof useAcademicsAssessmentDetailsHook>[1]
  );

  const assessmentFormSchema = useMemo(
    () => getAssessmentFormSchema(translateText),
    [translateText]
  );

  const [selectedClassId, setSelectedClassId] = useState<number | undefined>(
    preSelectedClassId
  );
  const [previousClassId, setPreviousClassId] = useState<number | undefined>(
    preSelectedClassId
  );

  const methods = useForm<AssessmentFormData>({
    resolver: zodResolver(assessmentFormSchema),
    defaultValues: {
      instituteId: '',
      name: '',
      description: '',
      assessmentType: 'quiz',
      status: 'draft',
      classId: preSelectedClassId,
      courseId: undefined,
      maxScore: 100,
      passingScore: 50,
      dueDate: null,
      weightage: 0,
      instructions: '',
      remarks: '',
      academicYear: new Date().getFullYear().toString(),
      subjectIds: [],
      studentIds: [],
      subjectScoreConfigs: [],
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const watchedClassId = watch('classId');
  const watchedSubjectIds = watch('subjectIds');
  const watchedStudentIds = watch('studentIds');

  // Get class details to determine grade/section for student filtering
  const classAssessment = useMemo(() => {
    return watchedClassId ? getClassAssessmentById(watchedClassId) : undefined;
  }, [watchedClassId, getClassAssessmentById]);

  // Extract grade and section from class for API filtering
  const studentListParams = useMemo(() => {
    const params: { page: number; limit: number; grade?: string; section?: string } = {
      page: 1,
      limit: 1000,
    };

    if (classAssessment?.classDetails?.standardLevel) {
      const standardLevel = classAssessment.classDetails.standardLevel;
      const gradeMatch = standardLevel.match(/\d+/);
      if (gradeMatch) {
        params.grade = gradeMatch[0];
      }
    }

    if (classAssessment?.classDetails?.section) {
      params.section = classAssessment.classDetails.section;
    }

    return params;
  }, [classAssessment]);

  const { data: studentListResponse } = useStudentListHook(studentListParams);

  // Get available subjects for selected class
  const availableSubjects = useMemo(() => {
    if (!watchedClassId) return [];
    const classStructure = getSubjectsByClassId(watchedClassId);
    return classStructure?.subjects || [];
  }, [watchedClassId, getSubjectsByClassId]);

  // Get available students - API returns filtered data, no client-side filtering needed
  const availableStudents = useMemo(() => {
    if (!studentListResponse?.data) return [];

    return studentListResponse.data.map((student) => ({
      id: student.id || '',
      name: `${student.basicDetails.firstName} ${student.basicDetails.lastName}`,
      basicDetails: student.basicDetails,
    }));
  }, [studentListResponse]);

  // Set default: select all subjects and students when class is selected (only on class change)
  useEffect(() => {
    if (watchedClassId !== previousClassId) {
      if (watchedClassId) {
        // Select all subjects when class is selected
        if (availableSubjects.length > 0) {
          const allSubjectIds = availableSubjects.map((s) => s.courseId);
          setValue('subjectIds', allSubjectIds);
        }
        // Select all students when class is selected
        if (availableStudents.length > 0) {
          const allStudentIds = availableStudents.map((s) => s.id).filter(Boolean);
          if (allStudentIds.length > 0) {
            setValue('studentIds', allStudentIds);
          }
        }
      } else {
        // Clear subjects and students when class is deselected
        setValue('subjectIds', []);
        setValue('studentIds', []);
      }
      setPreviousClassId(watchedClassId);
    } else if (watchedClassId && watchedClassId === previousClassId) {
      // Handle case when students/subjects load asynchronously after class is already selected
      // Only auto-select if current selection is empty (user hasn't manually changed it)
      if (availableSubjects.length > 0 && watchedSubjectIds.length === 0) {
        const allSubjectIds = availableSubjects.map((s) => s.courseId);
        setValue('subjectIds', allSubjectIds);
      }
      if (availableStudents.length > 0 && watchedStudentIds.length === 0) {
        const allStudentIds = availableStudents.map((s) => s.id).filter(Boolean);
        if (allStudentIds.length > 0) {
          setValue('studentIds', allStudentIds);
        }
      }
    }
  }, [watchedClassId, previousClassId, availableSubjects, availableStudents, watchedSubjectIds, watchedStudentIds, setValue]);

  useEffect(() => {
    if (open) {
      if (assessmentId && assessmentDetails) {
        // Edit mode: populate form with existing assessment data
        const subjectIds = assessmentDetails.subjects?.map((s) => s.courseId) || [];
        const studentIds = assessmentDetails.subjects?.[0]?.scores?.map((s) => s.studentDetails.id).filter(Boolean) || [];
        const classId = assessmentDetails.classId ? Number(assessmentDetails.classId) : preSelectedClassId;

        // Build subjectScoreConfigs from existing subjects
        const subjectScoreConfigs = assessmentDetails.subjects?.map((subject) => ({
          courseId: subject.courseId,
          courseName: subject.courseName,
          subjectCode: subject.subjectCode,
          maxScore: assessmentDetails.maxScore, // Use global maxScore for now
          passingScore: assessmentDetails.maxScore * 0.5, // Default passing score
          scheduledDuration: subject.scheduledDuration,
          scheduledDate: subject.scheduledDate ? dayjs(subject.scheduledDate) : null,
        })) || [];

        reset({
          instituteId: '', // Will be populated from context or user selection
          name: assessmentDetails.name || '',
          description: assessmentDetails.description || '',
          assessmentType: assessmentDetails.assessmentType as any,
          status: assessmentDetails.status as any,
          classId,
          courseId: assessmentDetails.courseId ? Number(assessmentDetails.courseId) : undefined,
          maxScore: assessmentDetails.maxScore || 100,
          passingScore: assessmentDetails.maxScore ? assessmentDetails.maxScore * 0.5 : 50,
          dueDate: assessmentDetails.dueDate ? dayjs(assessmentDetails.dueDate) : null,
          weightage: assessmentDetails.weightage || 0,
          instructions: assessmentDetails.instructions || '',
          remarks: assessmentDetails.remarks || '',
          academicYear: assessmentDetails.academicYear || new Date().getFullYear().toString(),
          subjectIds,
          studentIds,
          subjectScoreConfigs,
        });
        setSelectedClassId(classId);
        setPreviousClassId(classId);
      } else {
        // Add mode: use defaults
        const currentClassId = watchedClassId || preSelectedClassId;
        const defaultSubjectIds = currentClassId && availableSubjects.length > 0
          ? availableSubjects.map((s) => s.courseId)
          : [];

        const defaultStudentIds = currentClassId && availableStudents.length > 0
          ? availableStudents.map((s) => s.id).filter(Boolean)
          : [];

        reset({
          instituteId: '',
          name: '',
          description: '',
          assessmentType: 'quiz',
          status: 'draft',
          classId: preSelectedClassId,
          courseId: undefined,
          maxScore: 100,
          passingScore: 50,
          dueDate: null,
          weightage: 0,
          instructions: '',
          remarks: '',
          academicYear: new Date().getFullYear().toString(),
          subjectIds: defaultSubjectIds,
          studentIds: defaultStudentIds,
          subjectScoreConfigs: [],
        });
        setSelectedClassId(preSelectedClassId);
        setPreviousClassId(preSelectedClassId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset, preSelectedClassId, assessmentId, assessmentDetails, availableSubjects, availableStudents]);

  // Handle class selection change - update selectedClassId state
  useEffect(() => {
    if (watchedClassId !== selectedClassId) {
      setSelectedClassId(watchedClassId);
    }
  }, [watchedClassId, selectedClassId]);

  const assessmentTypeOptions = useMemo(
    () => getAssessmentTypeOptions(translateText),
    [translateText]
  );

  const statusOptions = useMemo(
    () => getStatusOptions(translateText),
    [translateText]
  );

  const handleSelectAllSubjects = () => {
    if (availableSubjects.length > 0) {
      const allSubjectIds = availableSubjects.map((s) => s.courseId);
      setValue('subjectIds', allSubjectIds);
    }
  };

  const handleDeselectAllSubjects = () => {
    setValue('subjectIds', []);
  };

  const handleSelectAllStudents = () => {
    if (availableStudents.length > 0) {
      const allStudentIds = availableStudents.map((s) => s.id).filter(Boolean);
      setValue('studentIds', allStudentIds);
    }
  };

  const handleDeselectAllStudents = () => {
    setValue('studentIds', []);
  };

  // Initialize subject score configs when subjects are selected
  const watchedMaxScore = watch('maxScore');
  const watchedPassingScore = watch('passingScore');
  const watchedSubjectScoreConfigs = watch('subjectScoreConfigs');

  useEffect(() => {
    if (watchedSubjectIds.length > 0 && availableSubjects.length > 0) {
      const configs: SubjectScoreConfig[] = [];

      watchedSubjectIds.forEach((subjectId) => {
        const subject = availableSubjects.find((s) => s.courseId === subjectId);
        if (!subject) return;

        // Check if config already exists
        const existingConfig = watchedSubjectScoreConfigs?.find(
          (c) => c.courseId === subjectId
        );

        configs.push({
          courseId: subject.courseId,
          courseName: subject.courseName,
          subjectCode: subject.subjectCode,
          maxScore: existingConfig?.maxScore ?? watchedMaxScore,
          passingScore: existingConfig?.passingScore ?? watchedPassingScore,
          scheduledDuration: existingConfig?.scheduledDuration,
          scheduledDate: existingConfig?.scheduledDate ?? null,
        });
      });

      setValue('subjectScoreConfigs', configs);
    }
  }, [watchedSubjectIds, availableSubjects, watchedMaxScore, watchedPassingScore, watchedSubjectScoreConfigs, setValue]);

  const onSubmit = async (data: AssessmentFormData) => {
    try {
      // Build subject score configs if not already set
      let subjectScoreConfigs = data.subjectScoreConfigs;
      if (!subjectScoreConfigs || subjectScoreConfigs.length === 0) {
        subjectScoreConfigs = data.subjectIds.map((subjectId) => {
          const subject = availableSubjects.find((s) => s.courseId === subjectId);
          return {
            courseId: subjectId,
            courseName: subject?.courseName || '',
            subjectCode: subject?.subjectCode,
            maxScore: data.maxScore,
            passingScore: data.passingScore,
            scheduledDuration: undefined, // Duration can be set per subject
            scheduledDate: null, // Scheduled date can be set per subject
          };
        });
      }

      const payload: IAssessmentRequest = {
        assessment: {
          instituteId: data.instituteId,
          name: data.name,
          description: data.description,
          assessmentType: data.assessmentType,
          status: data.status || 'draft',
          classId: data.classId ? String(data.classId) : undefined,
          maxScore: data.maxScore,
          dueDate: data.dueDate
            ? dayjs(data.dueDate).toISOString()
            : undefined,
          weightage: data.weightage,
          instructions: data.instructions,
          remarks: data.remarks,
          academicYear: data.academicYear,
          subjectIds: data.subjectIds.length > 0 ? data.subjectIds : undefined,
          studentIds: data.studentIds.length > 0 ? data.studentIds : undefined,
          passingScore: data.passingScore,
          // Include subject score configs for IStudentSubjectAssessment structure
          subjectScoreConfigs: subjectScoreConfigs.map((config) => ({
            courseId: config.courseId,
            maxScore: config.maxScore,
            passingScore: config.passingScore,
            scheduledDuration: config.scheduledDuration,
            scheduledDate: config.scheduledDate
              ? dayjs(config.scheduledDate).toISOString()
              : undefined,
          })),
        },
      };

      if (assessmentId) {
        await updateAssessment(assessmentId, payload);
      } else {
        await createAssessment(payload);
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to create assessment:', error);
      throw error;
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <StructuredModal
      isOpen={open}
      onClose={handleClose}
      title={
        assessmentId
          ? translateText('academics.assessments.editAssessment')
          : translateText('academics.assessments.addAssessment')
      }
      drawerWidth="70vw"
      footerNegativeAction={handleClose}
      footerNegativeActionText={translateText('common.cancel')}
      footerPositiveAction={handleSubmit(onSubmit)}
      footerPositiveActionText={
        assessmentId
          ? translateText('common.update')
          : translateText('common.create')
      }
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Basic Information Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {translateText('academics.assessments.basicInformation')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={3}>
                <ControlledTextField
                  id="instituteId"
                  name="instituteId"
                  label={translateText('events.instituteId')}
                  control={control}
                  errorMessage={errors.instituteId?.message}
                  isFullWidth
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <ControlledSelectField
                  id="assessmentType"
                  name="assessmentType"
                  label={translateText('academics.assessments.assessmentType')}
                  control={control}
                  itemList={assessmentTypeOptions}
                  isRequired
                  errorMessage={errors.assessmentType?.message}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="name"
                  name="name"
                  label={translateText('academics.assessments.assessmentName')}
                  control={control}
                  errorMessage={errors.name?.message}
                  isFullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="description"
                  name="description"
                  label={translateText('academics.assessments.description')}
                  control={control}
                  multiline
                  rows={3}
                  errorMessage={errors.description?.message}
                  isFullWidth
                />
              </Grid>

              {/* Class/Course Selection */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {translateText('academics.assessments.class')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="classId"
                  name="classId"
                  label={translateText('academics.assessments.class')}
                  control={control}
                  itemList={classOptions}
                  isRequired={true}
                  errorMessage={errors.classId?.message}
                  onChangeHandler={(value) => {
                    setSelectedClassId(value ? Number(value) : undefined);
                  }}
                />
              </Grid>

              {/* Assessment Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {translateText('academics.assessments.assessmentDetails')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={3}>
                <ControlledTextField
                  id="maxScore"
                  name="maxScore"
                  label={translateText('academics.assessments.maxScore')}
                  control={control}
                  type="number"
                  errorMessage={errors.maxScore?.message}
                  isFullWidth
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <ControlledTextField
                  id="passingScore"
                  name="passingScore"
                  label={translateText('academics.assessments.passingScore') || 'Passing Score'}
                  control={control}
                  type="number"
                  errorMessage={errors.passingScore?.message}
                  isFullWidth
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <ControlledTextField
                  id="weightage"
                  name="weightage"
                  label={translateText('academics.assessments.weightage')}
                  control={control}
                  type="number"
                  errorMessage={errors.weightage?.message}
                  isFullWidth
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <ControlledDateField
                  id="dueDate"
                  name="dueDate"
                  label={translateText('academics.assessments.dueDate')}
                  control={control}
                  errorMessage={errors.dueDate?.message as string}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <ControlledSelectField
                  id="status"
                  name="status"
                  label={translateText('academics.assessments.statusLabel')}
                  control={control}
                  itemList={statusOptions}
                  errorMessage={errors.status?.message}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <ControlledTextField
                  id="academicYear"
                  name="academicYear"
                  label={translateText('academics.assessments.academicYear')}
                  control={control}
                  errorMessage={errors.academicYear?.message}
                  isFullWidth
                />
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <ControlledTextField
                  id="instructions"
                  name="instructions"
                  label={translateText('academics.assessments.instructions')}
                  control={control}
                  multiline
                  rows={4}
                  errorMessage={errors.instructions?.message}
                  isFullWidth
                />
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <ControlledTextField
                  id="remarks"
                  name="remarks"
                  label={translateText('academics.assessments.columns.remarks') || 'Remarks'}
                  control={control}
                  multiline
                  rows={3}
                  errorMessage={errors.remarks?.message}
                  isFullWidth
                  placeholder={translateText('academics.assessments.optionalRemarksPlaceholder') || 'Optional assessment-level remarks'}
                />
              </Grid>

              {/* Subjects Selection and Configuration Section */}
              {watchedClassId && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      {translateText('academics.assessments.selectSubjects') || 'Select Subjects'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {translateText('academics.assessments.subjectScoreConfigurationDescription') || 'Select subjects and configure max score, passing score, and duration for each subject. Leave blank to use global values.'}
                    </Typography>
                    <Divider sx={{ mb: 2, mt: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, maxHeight: '600px', overflow: 'auto' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          {translateText('academics.assessments.selectSubjectsForAssessment') || 'Select subjects for this assessment'}
                        </Typography>
                        <Box>
                          <Button
                            size="small"
                            onClick={handleSelectAllSubjects}
                            sx={{ mr: 1 }}
                            data-testid="select-all-subjects-button"
                          >
                            {translateText('common.selectAll') || 'Select All'}
                          </Button>
                          <Button
                            size="small"
                            onClick={handleDeselectAllSubjects}
                            data-testid="deselect-all-subjects-button"
                          >
                            {translateText('common.deselectAll') || 'Deselect All'}
                          </Button>
                        </Box>
                      </Box>
                      <Controller
                        name="subjectIds"
                        control={control}
                        render={({ field }) => (
                          <Grid container spacing={2}>
                            {availableSubjects.map((subject) => {
                              const isSelected = field.value.includes(subject.courseId);
                              const config = watch('subjectScoreConfigs')?.find(
                                (c) => c.courseId === subject.courseId
                              );
                              const globalMaxScore = watch('maxScore');
                              const globalPassingScore = watch('passingScore');
                              // Find the index in the selected subjects array for the form field name
                              const configIndex = isSelected ? field.value.indexOf(subject.courseId) : -1;

                              return (
                                <Grid item xs={12} key={subject.courseId}>
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      p: 2,
                                      bgcolor: isSelected ? 'background.default' : 'action.disabledBackground',
                                      opacity: isSelected ? 1 : 0.7,
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={isSelected}
                                            onChange={(e) => {
                                              const currentValue = field.value || [];
                                              const currentConfigs = watch('subjectScoreConfigs') || [];

                                              if (e.target.checked) {
                                                // Add subject to selection
                                                field.onChange([...currentValue, subject.courseId]);

                                                // Initialize config for this subject if it doesn't exist
                                                if (!currentConfigs.find((c) => c.courseId === subject.courseId)) {
                                                  const newConfig: SubjectScoreConfig = {
                                                    courseId: subject.courseId,
                                                    courseName: subject.courseName,
                                                    subjectCode: subject.subjectCode,
                                                    maxScore: globalMaxScore,
                                                    passingScore: globalPassingScore,
                                                    scheduledDuration: undefined,
                                                    scheduledDate: null,
                                                  };
                                                  setValue('subjectScoreConfigs', [...currentConfigs, newConfig]);
                                                }
                                              } else {
                                                // Remove subject from selection
                                                field.onChange(
                                                  currentValue.filter((id) => id !== subject.courseId)
                                                );

                                                // Remove config for this subject
                                                setValue(
                                                  'subjectScoreConfigs',
                                                  currentConfigs.filter((c) => c.courseId !== subject.courseId)
                                                );
                                              }
                                            }}
                                            data-testid={`subject-checkbox-${subject.courseId}`}
                                          />
                                        }
                                        label={
                                          <Typography variant="subtitle2" fontWeight="bold">
                                            {subject.courseName} {subject.subjectCode && `(${subject.subjectCode})`}
                                          </Typography>
                                        }
                                      />
                                    </Box>
                                    {isSelected && configIndex >= 0 && (
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} sm={3}>
                                          <ControlledTextField
                                            id={`subject-maxScore-${subject.courseId}`}
                                            name={`subjectScoreConfigs.${configIndex}.maxScore`}
                                            label={translateText('academics.assessments.maxScore')}
                                            control={control}
                                            type="number"
                                            defaultValue={config?.maxScore ?? globalMaxScore}
                                            isFullWidth
                                            data-testid={`subject-maxScore-${subject.courseId}`}
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                          <ControlledTextField
                                            id={`subject-passingScore-${subject.courseId}`}
                                            name={`subjectScoreConfigs.${configIndex}.passingScore`}
                                            label={translateText('academics.assessments.passingScore') || 'Passing Score'}
                                            control={control}
                                            type="number"
                                            defaultValue={config?.passingScore ?? globalPassingScore}
                                            isFullWidth
                                            data-testid={`subject-passingScore-${subject.courseId}`}
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                          <ControlledTextField
                                            id={`subject-duration-${subject.courseId}`}
                                            name={`subjectScoreConfigs.${configIndex}.scheduledDuration`}
                                            label={translateText('academics.assessments.durationMinutes') || 'Duration (minutes)'}
                                            control={control}
                                            type="number"
                                            defaultValue={config?.scheduledDuration}
                                            isFullWidth
                                            data-testid={`subject-duration-${subject.courseId}`}
                                          />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                          <ControlledDateField
                                            id={`subject-scheduledDate-${subject.courseId}`}
                                            name={`subjectScoreConfigs.${configIndex}.scheduledDate`}
                                            label={translateText('academics.assessments.scheduledDate')}
                                            control={control}
                                            errorMessage={errors.subjectScoreConfigs?.[configIndex]?.scheduledDate?.message as string}
                                            isGridItem={false}
                                          />
                                        </Grid>
                                      </Grid>
                                    )}
                                  </Paper>
                                </Grid>
                              );
                            })}
                          </Grid>
                        )}
                      />
                      {errors.subjectIds && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                          {errors.subjectIds.message}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                </>
              )}

              {/* Students Selection Section */}
              {watchedClassId && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      {translateText('academics.assessments.selectStudents') || 'Select Students'}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, maxHeight: '300px', overflow: 'auto' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          {translateText('academics.assessments.selectStudentsForAssessment') || 'Select students for this assessment (default: all students)'}
                        </Typography>
                        <Box>
                          <Button
                            size="small"
                            onClick={handleSelectAllStudents}
                            sx={{ mr: 1 }}
                            data-testid="select-all-students-button"
                          >
                            {translateText('common.selectAll') || 'Select All'}
                          </Button>
                          <Button
                            size="small"
                            onClick={handleDeselectAllStudents}
                            data-testid="deselect-all-students-button"
                          >
                            {translateText('common.deselectAll') || 'Deselect All'}
                          </Button>
                        </Box>
                      </Box>
                      <Controller
                        name="studentIds"
                        control={control}
                        render={({ field }) => (
                          <FormGroup>
                            {availableStudents.map((student) => (
                              <FormControlLabel
                                key={student.id}
                                control={
                                  <Checkbox
                                    checked={field.value.includes(student.id)}
                                    onChange={(e) => {
                                      const currentValue = field.value || [];
                                      if (e.target.checked) {
                                        field.onChange([...currentValue, student.id]);
                                      } else {
                                        field.onChange(
                                          currentValue.filter((id) => id !== student.id)
                                        );
                                      }
                                    }}
                                    data-testid={`student-checkbox-${student.id}`}
                                  />
                                }
                                label={student.name}
                              />
                            ))}
                          </FormGroup>
                        )}
                      />
                    </Paper>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </form>
      </FormProvider>
    </StructuredModal>
  );
};

export default AddEditAssessmentView;
